// Import payload tree parser and dependency map builder.
// Converts the raw JSON payload from GET /import/:id into a navigable
// tree structure for the ImportTree component.
//
// Pure functions — no React, no side effects, fully unit-testable.

import type { ImportValidationError } from '@/lib/api/imports'

// --- Types ---

export interface TreeNode {
  /** Unique path: "section/subsection/token" e.g. "objects/risks/risk-1" */
  id: string
  /** Section path e.g. "objects/risks" (empty string for top-level sections) */
  section: string
  /** Item token e.g. "risk-1" (empty for section nodes) */
  token: string
  /** Display label (token, name, title, or section name) */
  label: string
  /** Node kind */
  type: 'section' | 'item'
  /** Child nodes (items under sections, subsections under top-level) */
  children: TreeNode[]
  /** Raw item data for the edit form (only on item nodes) */
  itemData?: Record<string, unknown>
  /** True if this node or any descendant has validation errors */
  hasErrors: boolean
  /** Validation errors directly on this node */
  errors: ImportValidationError[]
  /** Item type key for field schema lookup (e.g. "risk", "control", "lookup") */
  itemType?: string
}

/**
 * Dependency map: token → set of tokens that depend on it.
 * Used for warning when removing an item that others reference.
 */
export type DependencyMap = Map<string, Set<string>>

// --- Section definitions ---

/** Maps payload keys/paths to display names and item type keys. */
interface SectionDef {
  /** Path used as section id prefix, e.g. "structure/registers" */
  path: string
  /** Display name for the section header */
  label: string
  /** Key to extract items from the payload */
  payloadPath: string[]
  /** Item type key for field schema lookup */
  itemType: string
  /** Field to use as display label (falls back to "token") */
  labelField: string
}

const SECTION_DEFS: SectionDef[] = [
  // Lookups
  { path: 'lookups', label: 'Lookups', payloadPath: ['lookups'], itemType: 'lookup', labelField: 'token' },

  // Structure
  { path: 'structure/users', label: 'Users', payloadPath: ['structure', 'users'], itemType: 'user', labelField: 'display_name' },
  { path: 'structure/ownership_roles', label: 'Ownership Roles', payloadPath: ['structure', 'ownership_roles'], itemType: 'ownership_role', labelField: 'name' },
  { path: 'structure/registers', label: 'Registers', payloadPath: ['structure', 'registers'], itemType: 'register', labelField: 'name' },
  { path: 'structure/control_categories', label: 'Control Categories', payloadPath: ['structure', 'control_categories'], itemType: 'category', labelField: 'name' },
  { path: 'structure/event_categories', label: 'Event Categories', payloadPath: ['structure', 'event_categories'], itemType: 'category', labelField: 'name' },
  { path: 'structure/action_categories', label: 'Action Categories', payloadPath: ['structure', 'action_categories'], itemType: 'category', labelField: 'name' },
  { path: 'structure/framework_categories', label: 'Framework Categories', payloadPath: ['structure', 'framework_categories'], itemType: 'category', labelField: 'name' },
  { path: 'structure/event_types', label: 'Event Types', payloadPath: ['structure', 'event_types'], itemType: 'event_type', labelField: 'name' },

  // Permissions
  { path: 'permissions/roles', label: 'Permission Roles', payloadPath: ['permissions', 'roles'], itemType: 'permission_role', labelField: 'name' },
  { path: 'permissions/grants', label: 'Permission Grants', payloadPath: ['permissions', 'grants'], itemType: 'permission_grant', labelField: 'role' },
  { path: 'permissions/assignments', label: 'Role Assignments', payloadPath: ['permissions', 'assignments'], itemType: 'permission_assignment', labelField: 'role' },

  // Objects
  { path: 'objects/risks', label: 'Risks', payloadPath: ['objects', 'risks'], itemType: 'risk', labelField: 'name' },
  { path: 'objects/controls', label: 'Controls', payloadPath: ['objects', 'controls'], itemType: 'control', labelField: 'name' },
  { path: 'objects/events', label: 'Events', payloadPath: ['objects', 'events'], itemType: 'event', labelField: 'title' },
  { path: 'objects/actions', label: 'Actions', payloadPath: ['objects', 'actions'], itemType: 'action', labelField: 'title' },

  // Supplementary
  { path: 'appetite', label: 'Risk Appetite', payloadPath: ['appetite'], itemType: 'appetite', labelField: 'taxonomy' },
  { path: 'assessments', label: 'Assessments', payloadPath: ['assessments'], itemType: 'assessment', labelField: 'control' },
  { path: 'updates', label: 'Action Updates', payloadPath: ['updates'], itemType: 'update', labelField: 'action' },

  // Obligations
  { path: 'obligations', label: 'Obligation Frameworks', payloadPath: ['obligations'], itemType: 'obligation_framework', labelField: 'name' },
]

// --- Tree building ---

/**
 * Extracts a nested value from an object using a key path.
 * Returns undefined if any segment is missing.
 */
function getNestedValue(obj: Record<string, unknown>, path: string[]): unknown {
  let current: unknown = obj
  for (const key of path) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

/**
 * Get the display label for an item, falling back through options.
 */
function getItemLabel(item: Record<string, unknown>, labelField: string): string {
  const primary = item[labelField]
  if (typeof primary === 'string' && primary) return primary
  const token = item['token']
  if (typeof token === 'string' && token) return token
  return '(unnamed)'
}

/**
 * Get the token for an item, using the 'token' field or generating a positional one.
 */
function getItemToken(item: Record<string, unknown>, index: number): string {
  const token = item['token']
  if (typeof token === 'string' && token) return token
  return `item-${index}`
}

/**
 * Build the tree from an import payload and validation errors.
 * Returns section nodes (with item children), including empty sections.
 */
export function buildImportTree(
  payload: Record<string, unknown>,
  validationErrors: ImportValidationError[],
): TreeNode[] {
  // Build error lookup: "section/token" → errors
  const errorMap = buildErrorMap(validationErrors)

  // Group sections by top-level category for nesting
  const topGroups: Map<string, TreeNode[]> = new Map()
  const topLevelSections: TreeNode[] = []

  for (const def of SECTION_DEFS) {
    const items = getNestedValue(payload, def.payloadPath)
    const itemArray = Array.isArray(items) ? items : []

    const children: TreeNode[] = itemArray.map((item, idx) => {
      const rawItem = item as Record<string, unknown>
      const token = getItemToken(rawItem, idx)
      const nodeId = `${def.path}/${token}`
      const nodeErrors = errorMap.get(nodeId) ?? []

      return {
        id: nodeId,
        section: def.path,
        token,
        label: getItemLabel(rawItem, def.labelField),
        type: 'item' as const,
        children: [],
        itemData: rawItem,
        hasErrors: nodeErrors.length > 0,
        errors: nodeErrors,
        itemType: def.itemType,
      }
    })

    // Check for section-level errors (errors that reference the section but no specific token)
    const sectionErrors = errorMap.get(def.path) ?? []

    const sectionNode: TreeNode = {
      id: def.path,
      section: '',
      token: '',
      label: def.label,
      type: 'section',
      children,
      hasErrors: sectionErrors.length > 0 || children.some((c) => c.hasErrors),
      errors: sectionErrors,
    }

    // Determine grouping: "structure/users" → top group "structure"
    const parts = def.path.split('/')
    if (parts.length > 1 && parts[0]) {
      const groupKey: string = parts[0]
      if (!topGroups.has(groupKey)) {
        topGroups.set(groupKey, [])
      }
      topGroups.get(groupKey)!.push(sectionNode)
    } else {
      topLevelSections.push(sectionNode)
    }
  }

  // Build top-level group nodes (structure, permissions, objects)
  const result: TreeNode[] = []

  // Lookups is standalone
  const lookupSection = topLevelSections.find((s) => s.id === 'lookups')
  if (lookupSection) result.push(lookupSection)

  // Grouped sections
  const groupLabels: Record<string, string> = {
    structure: 'Structure',
    permissions: 'Permissions',
    objects: 'Objects',
  }

  for (const [groupKey, groupChildren] of topGroups) {
    const groupNode: TreeNode = {
      id: groupKey,
      section: '',
      token: '',
      label: groupLabels[groupKey] ?? groupKey,
      type: 'section',
      children: groupChildren,
      hasErrors: groupChildren.some((c) => c.hasErrors),
      errors: [],
    }
    result.push(groupNode)
  }

  // Remaining standalone sections (appetite, assessments, updates, obligations)
  for (const section of topLevelSections) {
    if (section.id !== 'lookups') {
      result.push(section)
    }
  }

  return result
}

/**
 * Build a lookup from "section/token" → validation errors.
 * Errors with an empty token are keyed by section only.
 */
function buildErrorMap(errors: ImportValidationError[]): Map<string, ImportValidationError[]> {
  const map = new Map<string, ImportValidationError[]>()
  for (const err of errors) {
    const key = err.token ? `${err.section}/${err.token}` : err.section
    const existing = map.get(key)
    if (existing) {
      existing.push(err)
    } else {
      map.set(key, [err])
    }
  }
  return map
}

// --- Dependency map ---

/**
 * Fields in each item type that contain token references.
 * Key = itemType from SectionDef, value = array of field names containing tokens.
 */
const TOKEN_REF_FIELDS: Record<string, string[]> = {
  // Structure
  ownership_role: ['owner'],
  register: ['parent', 'owner'],
  category: ['parent', 'owner'],
  event_type: [],

  // Permissions
  permission_role: [],
  permission_grant: ['role', 'node'],
  permission_assignment: ['role', 'users'],

  // Objects
  risk: ['register', 'taxonomy', 'owner', 'created_by'],
  control: ['category', 'owner', 'created_by', 'risks'],
  event: ['event_type', 'category', 'recorded_by', 'risks', 'actions'],
  action: ['category', 'owner', 'created_by', 'risks', 'controls'],

  // Supplementary
  appetite: ['taxonomy'],
  assessment: ['control', 'assessed_by'],
  update: ['action', 'created_by'],

  // Obligations
  obligation_framework: ['category'],
}

/**
 * Build a dependency map from the tree.
 * Returns: dependedUpon token → set of dependent tokens.
 *
 * For example, if risk "risk-1" references register "reg-ops",
 * the map will contain "reg-ops" → Set(["risk-1"]).
 */
export function buildDependencyMap(tree: TreeNode[]): DependencyMap {
  const map: DependencyMap = new Map()

  function addDep(target: string, dependent: string) {
    if (!target || target === dependent) return
    const existing = map.get(target)
    if (existing) {
      existing.add(dependent)
    } else {
      map.set(target, new Set([dependent]))
    }
  }

  function processNode(node: TreeNode) {
    if (node.type === 'item' && node.itemData && node.itemType) {
      const refFields = TOKEN_REF_FIELDS[node.itemType]
      if (refFields) {
        const itemToken = node.token
        for (const field of refFields) {
          const value = node.itemData[field]
          if (typeof value === 'string' && value) {
            addDep(value, itemToken)
          } else if (Array.isArray(value)) {
            for (const v of value) {
              if (typeof v === 'string' && v) {
                addDep(v, itemToken)
              }
            }
          }
        }
      }
    }

    for (const child of node.children) {
      processNode(child)
    }
  }

  for (const root of tree) {
    processNode(root)
  }

  return map
}

// --- Count helpers ---

/**
 * Count the total number of item nodes in the tree (excluding section nodes).
 */
export function countItems(tree: TreeNode[]): number {
  let count = 0
  function walk(nodes: TreeNode[]) {
    for (const node of nodes) {
      if (node.type === 'item') count++
      walk(node.children)
    }
  }
  walk(tree)
  return count
}

/**
 * Count the total number of items with errors.
 */
export function countErrors(tree: TreeNode[]): number {
  let count = 0
  function walk(nodes: TreeNode[]) {
    for (const node of nodes) {
      if (node.type === 'item' && node.hasErrors) count++
      walk(node.children)
    }
  }
  walk(tree)
  return count
}
